/**
 * Extract text is a verbatim span of a source's extracted markdown, and Document Intelligence
 * writes tables into that markdown as HTML (`<table><tr><td>…`) with `<!-- … -->` comments for
 * page furniture and HTML entities for escaped characters. None of that is visible in a rendered
 * document, so extract text is stripped before it is shown or matched against a render.
 */

const ENTITIES: Record<string, string> = {amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " "};

/** The character for `code`, or `undefined` when it is out of range or a surrogate. */
const fromCodePoint = (code: number): string | undefined =>
  Number.isInteger(code) && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff)
    ? String.fromCodePoint(code)
    : undefined;

const decodeEntity = (body: string): string | undefined => {
  if (body.startsWith("#x") || body.startsWith("#X")) return fromCodePoint(parseInt(body.slice(2), 16));
  if (body.startsWith("#")) return fromCodePoint(parseInt(body.slice(1), 10));
  return Object.hasOwn(ENTITIES, body) ? ENTITIES[body] : undefined;
};

// A comment, a lowercase tag as Document Intelligence writes it (capturing its name and what follows
// it), or an entity (capturing its body). A name must end at whitespace, `/` or `>`, so
// `<a.n.other@acme.com>` is text.
const TOKEN = /<!--[\s\S]*?-->|<\/?([a-z][a-z0-9]*)(\s[^<>]*)?\/?>|&(#[0-9]+|#[xX][0-9a-fA-F]+|[a-zA-Z]+);/g;

// After a tag's name, only attributes with values and a self-closing slash, so the `<b and c>` of
// `a<b and c>d` and `<table of contents>` are text. Values are quoted, except on a table tag,
// where an unquoted one such as `colspan=2` is allowed as HTML allows it.
const QUOTED_ATTRIBUTES = /^(?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*\s*=\s*(?:"[^"]*"|'[^']*'))*\s*\/?$/;
const VALUED_ATTRIBUTES = /^(?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'<>/=]+))*\s*\/?$/;
const TABLE_TAGS = new Set(["table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption"]);
const isMarkup = (tag: string, attributes: string | undefined) =>
  attributes === undefined || (TABLE_TAGS.has(tag) ? VALUED_ATTRIBUTES : QUOTED_ATTRIBUTES).test(attributes);

const CELL_TAGS = new Set(["td", "th"]);
const BLOCK_TAGS = new Set([
  "tr",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "p",
  "div",
  "figure",
  "figcaption",
  "caption",
  "li",
  "ul",
  "ol",
]);
// Stripped without a boundary. Any other `<…>` is text: an address, a placeholder.
const INLINE_TAGS = new Set(["b", "i", "u", "em", "strong", "span", "sup", "sub"]);

/** Stripping marks where a table cell starts, where a block ends and where a line breaks; each caller
 * decides what they become. */
const CELL = "\u0001";
const BLOCK = "\u0002";
const BREAK = "\u0003";

type Stripped = {text: string; toOriginal: number[]};

const strip = (text: string): Stripped => {
  const chars: string[] = [];
  const toOriginal: number[] = [];
  const push = (s: string, at: number) => {
    for (let i = 0; i < s.length; i++) {
      chars.push(s[i]);
      toOriginal.push(at);
    }
  };
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const at = match.index;
    for (let i = last; i < at; i++) push(text[i], i);
    last = at + match[0].length;
    const [token, tag, attributes, entity] = match;
    if (entity !== undefined) {
      push(decodeEntity(entity) ?? token, at);
    } else if (tag !== undefined && !isMarkup(tag, attributes)) {
      for (let i = 0; i < token.length; i++) push(token[i], at + i);
    } else if (tag !== undefined) {
      if (CELL_TAGS.has(tag)) {
        if (!token.startsWith("</")) push(CELL, at);
      } else if (BLOCK_TAGS.has(tag)) push(BLOCK, at);
      else if (tag === "br") push(BREAK, at);
      else if (!INLINE_TAGS.has(tag)) for (let i = 0; i < token.length; i++) push(token[i], at + i);
    } else {
      push(BLOCK, at);
    }
  }
  for (let i = last; i < text.length; i++) push(text[i], i);
  return {text: chars.join(""), toOriginal};
};

/**
 * Strip `text`'s markup for matching against a render. Cells and blocks become whitespace, and
 * `toOriginal[i]` is the index in `text` of stripped character `i`.
 */
export function stripMarkupWithMap(text: string): Stripped {
  const stripped = strip(text);
  return {
    text: stripped.text.replaceAll(CELL, " ").replaceAll(BLOCK, "\n").replaceAll(BREAK, "\n"),
    toOriginal: stripped.toOriginal,
  };
}

const collapse = (s: string) => s.replace(/\s+/g, " ").trim();

/** Show `text` as a reader should see it, with a table row's cells joined by " · ". */
export function displayExtractText(text: string): string {
  return (
    strip(text)
      .text.split(BLOCK)
      // A table row keeps its cells, line breaks within them included, on one line; prose keeps its
      // own line breaks.
      .flatMap(block =>
        block.includes(CELL)
          ? [
              block
                .replaceAll(BREAK, " ")
                .split(CELL)
                .map(collapse)
                .filter(cell => cell.length > 0)
                .join(" · "),
            ]
          : block.replaceAll(BREAK, "\n").split("\n").map(collapse),
      )
      .filter(line => line.length > 0)
      .join("\n")
  );
}
